// Import crypto polyfill FIRST before any other imports that might use uuid/crypto
import 'react-native-get-random-values';

import React, { useEffect, useState } from 'react';
import {
  Button,
  KeyboardAvoidingView,
  PermissionsAndroid,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Toast, {
  BaseToast,
  ErrorToast,
  ToastConfigParams,
} from 'react-native-toast-message';
import { Provider, useSelector } from 'react-redux';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { ShowToast } from './Common/common';
import ErrorBoundary from './Components/ErrorBoundary';
import Home from './Screens/Home';
import store from './redux/store';
import { RootState } from './redux/store';
import { sseClient } from './services/sseClient';
import {
  checkAppUpdateStatus,
  handleUpdate,
  UpdateResult,
} from './utils/updateChecker';
import UpdateModal from './Components/UpdateModal';
import { initI18n } from './i18n';


import T from './Components/T';
export const toastConfig = {
  success: (props: any) => (
    <BaseToast
      {...props}
      style={{
        borderLeftColor: 'green',
        backgroundColor: '#03C988',
        borderLeftWidth: 4,
        minHeight: 60,
        zIndex: 999999,
        elevation: Platform.OS === 'android' ? 100 : undefined,
        justifyContent: 'center',
        alignItems: 'center',
        ...(Platform.OS === 'web' && {
          position: 'fixed',
          top: 10,
          left: 0,
          right: 0,
          zIndex: 999999,
        }),
      }}
      contentContainerStyle={{
        paddingHorizontal: 15,
        paddingVertical: 12,
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        flexWrap: 'wrap',
      }}
      text1Style={{
        fontSize: 18,
        fontWeight: '400',
        color: '#FFFFFF',
        textAlign: 'center',
      }}
      text2Style={{
        fontSize: 15,
        color: '#FFFFFF',
        textAlign: 'center',
      }}
    />
  ),
  error: (props: any) => (
    <ErrorToast
      {...props}
      style={{
        borderLeftColor: 'red',
        backgroundColor: '#FF2171',
        borderLeftWidth: 4,
        minHeight: 60,
        zIndex: 999999,
        elevation: Platform.OS === 'android' ? 100 : undefined,
        justifyContent: 'center',
        alignItems: 'center',
        ...(Platform.OS === 'web' && {
          position: 'fixed',
          top: 10,
          left: 0,
          right: 0,
          zIndex: 999999,
        }),
      }}
      contentContainerStyle={{
        paddingHorizontal: 15,
        paddingVertical: 12,
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        flexWrap: 'wrap',
      }}
      text1Style={{
        fontSize: 18,
        fontWeight: '400',
        color: '#FFFFFF',
        textAlign: 'center',
      }}
      text2Style={{
        fontSize: 15,
        color: '#FFFFFF',
        textAlign: 'center',
      }}
    />
  ),
  warning: (props: any) => (
    <BaseToast
      {...props}
      style={{
        borderLeftColor: '#F59E0B',
        backgroundColor: '#F59E0B',
        borderLeftWidth: 4,
        minHeight: 60,
        zIndex: 999999,
        elevation: Platform.OS === 'android' ? 100 : undefined,
        justifyContent: 'center',
        alignItems: 'center',
        ...(Platform.OS === 'web' && {
          position: 'fixed',
          top: 10,
          left: 0,
          right: 0,
          zIndex: 999999,
        }),
      }}
      contentContainerStyle={{
        paddingHorizontal: 15,
        paddingVertical: 12,
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        flexWrap: 'wrap',
      }}
      text1Style={{
        fontSize: 18,
        fontWeight: '400',
        color: '#FFFFFF',
        textAlign: 'center',
      }}
      text2Style={{
        fontSize: 15,
        color: '#FFFFFF',
        textAlign: 'center',
      }}
    />
  ),
  tomato: ({text1 = '', props}: ToastConfigParams<any>) => (
    <View style={{height: 60, width: '100%', backgroundColor: 'tomato'}}>
      <Text style={{flexShrink: 1}}>{text1}</Text>
      <Text style={{flexShrink: 1}}>{props.uuid}</Text>
    </View>
  ),
};

const App: React.FC = () => {
  const [updateInfo, setUpdateInfo] = useState<UpdateResult | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    initI18n().finally(() => setI18nReady(true));
  }, []);

  const requestCameraPermission = async () => {
    if (Platform.OS === 'android') {
      try {
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.CAMERA,
          {
            title: 'Camera Permission',
            message: 'App needs access to your camera',
            buttonNeutral: 'Ask Me Later',
            buttonNegative: 'Cancel',
            buttonPositive: 'OK',
          },
        );
        return granted === PermissionsAndroid.RESULTS.GRANTED;
      } catch (err) {
        console.warn(err);
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    // Fonts are automatically loaded by @react-native-vector-icons
    // No need for manual font loading
    if (Platform.OS === 'web') {
      // Ensure Toast container is always on top on web
      const styleId = 'toast-z-index-fix';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          /* Ensure Toast is always on top - target all possible Toast containers */
          [class*="Toast"],
          [class*="toast"],
          [data-testid*="toast"],
          [data-testid*="Toast"] {
            z-index: 999999 !important;
            position: fixed !important;
          }
          /* Target react-native-toast-message root container */
          body > div:last-child,
          #root > div:last-child {
            z-index: 999999 !important;
          }
          /* Ensure Toast appears above modals */
          [class*="Toast"] * {
            z-index: 999999 !important;
          }
        `;
        document.head.appendChild(style);
      }

      // Also ensure Toast container is styled correctly after mount
      const styleToastElement = (element: HTMLElement) => {
        element.style.zIndex = '999999';
        element.style.position = 'fixed';
        // Also ensure all children have proper z-index
        const children = element.querySelectorAll('*');
        children.forEach((child: Element) => {
          if (child instanceof HTMLElement) {
            child.style.zIndex = '999999';
          }
        });
      };

      const ensureToastOnTop = () => {
        const toastContainers = document.querySelectorAll(
          '[class*="Toast"], [class*="toast"], [data-testid*="toast"], [data-testid*="Toast"]',
        );
        toastContainers.forEach((container: Element) => {
          if (container instanceof HTMLElement) {
            styleToastElement(container);
          }
        });
      };

      // Use MutationObserver to watch for Toast elements being added
      const observer = new MutationObserver((mutations: MutationRecord[]) => {
        mutations.forEach((mutation: MutationRecord) => {
          mutation.addedNodes.forEach((node: Node) => {
            if (node instanceof HTMLElement) {
              // Check if the added node is a Toast or contains Toast
              if (
                node.matches &&
                (node.matches('[class*="Toast"]') ||
                  node.matches('[class*="toast"]'))
              ) {
                styleToastElement(node);
              }
              // Also check children
              const toastElements =
                node.querySelectorAll &&
                node.querySelectorAll('[class*="Toast"], [class*="toast"]');
              if (toastElements) {
                toastElements.forEach((toastEl: Element) => {
                  if (toastEl instanceof HTMLElement) {
                    styleToastElement(toastEl);
                  }
                });
              }
            }
          });
        });
      });

      // Start observing the document body for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Check immediately and periodically
      ensureToastOnTop();
      const interval = setInterval(ensureToastOnTop, 500);

      let isMounted = true;

      // Camera permission check - skip on web
      const checkPermission = async () => {
        try {
          const hasPermission = await requestCameraPermission();
          if (!hasPermission) {
            ShowToast('Camera permission denied', 'error');
          }
        } catch (err) {
          console.warn('Permission check failed:', err);
        }
      };
      checkPermission();

      return () => {
        isMounted = false;
        observer.disconnect();
        clearInterval(interval);
      };
    } else {
      let isMounted = true;

      // Auto check for app updates immediately on app load
      const performUpdateCheck = async () => {
        try {
          const result = await checkAppUpdateStatus();
          if (isMounted && result.updateAvailable) {
            setUpdateInfo(result);
            setShowUpdateModal(true);
          }
        } catch (err) {
          console.warn('Auto update check error:', err);
        }
      };

      performUpdateCheck();

      const checkPermission = async () => {
        try {
          const hasPermission = await requestCameraPermission();
          if (!hasPermission) {
            ShowToast('Camera permission denied', 'error');
          }
        } catch (err) {
          console.warn('Permission check failed:', err);
        }
      };
      checkPermission();

      return () => {
        isMounted = false;
      };
    }
  }, []);

  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'web') {
      window.addEventListener('beforeinstallprompt', (e: any) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowInstall(true);
      });
    }
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choice: any) => {
        // Handle user choice
        setDeferredPrompt(null);
        setShowInstall(false);
      });
    }
  };

  if (showInstall)
    return <Button title="Install BlueHMS App" onPress={handleInstallClick} />;

  if (!i18nReady) {
    return (
      <ErrorBoundary>
        <Provider store={store}>
          <SafeAreaProvider style={styles.safeArea}>
            <KeyboardAvoidingView
              style={styles.container}
              behavior={Platform.OS === 'ios' ? 'padding' : undefined}
              keyboardVerticalOffset={Platform.OS === 'ios' ? 60 : 0}>
              <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Text><T>Loading...</T></Text>
              </View>
            </KeyboardAvoidingView>
          </SafeAreaProvider>
        </Provider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <SafeAreaProvider style={styles.safeArea}>
          <KeyboardAvoidingView
            style={styles.container}
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 60 : 0}>
            <Home />
          </KeyboardAvoidingView>
          {/* Toast - Top level component to appear above all elements, positioned below header */}
          <Toast
            config={toastConfig}
            position="top"
            topOffset={Platform.OS === 'ios' ? 60 : Platform.OS === 'web' ? 56 : 56}
            visibilityTime={4000}
            autoHide={true}
          />

          {/* Global Auto Update Modal */}
          {Platform.OS !== 'web' && updateInfo && (
            <UpdateModal
              visible={showUpdateModal}
              isForceUpdate={updateInfo.isForceUpdate}
              currentVersion={updateInfo.currentVersion}
              latestVersion={updateInfo.latestVersion}
              releaseNotes={updateInfo.releaseNotes}
              onUpdate={() => {
                handleUpdate(updateInfo.storeUrl);
              }}
              onLater={() => {
                setShowUpdateModal(false);
              }}
            />
          )}
        </SafeAreaProvider>
      </Provider>
    </ErrorBoundary>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    height: '100%',
  },
  container: {
    flex: 1,
    height: '100%',
  },
});

export default App;

